import React, { useState } from 'react';

import { queryClient } from 'App';
import { useRecoilValue } from 'recoil';

import { pm } from 'lib/helpers/utility';
import { useDeleteRole } from 'lib/hooks/queries/roles/useDeleteRole';
import { useEditRoleContext } from 'lib/context/Role/EditRoleContext/EditRoleContext';

import state from 'components/state';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import { withErrorBoundary } from 'components/shared/Error/Boundary';

import EditRoleModal from './EditRole/EditRoleModal';

const Actions = ({ data, isAdmin, forAdmin }) => {
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const { showEditRoleModal, setShowEditRoleModal } = useEditRoleContext();
  const permissions = useRecoilValue(state.permissions);
  const { id } = data;

  const { mutateAsync, isLoading } = useDeleteRole({
    params: {
      role_id: id,
      forAdmin
    },
    dependencies: [id],
    options: {
      onSuccess: (deleteResponse) => {
        setShowDeleteRoleModal(false);
        if (deleteResponse.code === 0) {
          showAlert({
            title: `Role deleted`,
            color: 'success'
          });
        } else {
          showAlert({
            title: `Couldn't delete the role`,
            color: 'danger'
          });
        }
        queryClient.invalidateQueries('getRole');
      }
    }
  });

  const deleteRoleMessage =
    data?.usersAssigned === '0'
      ? 'Are you sure you want to delete this role?'
      : 'You cannot delete this role because some users are still assigned to it. Please assign them a different role and try again.';

  const roleOptions = [
    ...(pm(permissions, forAdmin ? 'admin_role.update' : 'role.update')
      ? [
          {
            label: 'Edit role',
            icon: 'user-edit-mono',
            onClick: () => {
              setShowEditRoleModal(true);
            }
          }
        ]
      : []),
    ...(pm(permissions, forAdmin ? 'admin_role.delete' : 'role.delete')
      ? [
          {
            label: 'Delete role',
            icon: 'trash-mono',
            onClick: () => {
              setShowDeleteRoleModal(true);
            }
          }
        ]
      : [])
  ];

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={roleOptions}
        isBorder={false}
      />
      <Confirm
        isOpen={!!showDeleteRoleModal}
        handleClose={() => setShowDeleteRoleModal(false)}
        title="Delete role"
        message={deleteRoleMessage}
        icon="trash-mono"
        variant="danger"
        footerActions={{
          primary: {
            text: 'Delete Role',
            onClick: async () => {
              await mutateAsync();
            },
            color: 'danger',
            isDisabled: isLoading || data?.usersAssigned !== '0',
            isLoading
          },
          secondary: { text: 'Keep' }
        }}
      />
      <EditRoleModal
        showModal={!!showEditRoleModal}
        hideModal={() => setShowEditRoleModal(false)}
        roleName={data?.name}
        roleType={data?.type}
        roleDescription={data?.description}
        roleId={data?.id}
        isAdmin={isAdmin}
        forAdmin={forAdmin}
      />
    </>
  );
};

export default withErrorBoundary(Actions);
